import React, {
  useState,
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from 'react';
import Card from '@material-ui/core/Card';
import { Button, IconButton, Checkbox, TextField, MenuItem, Select } from '@material-ui/core';
import VolumeUpIcon from '@material-ui/icons/VolumeUp';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import DeleteIcon from '@material-ui/icons/Delete';
import ReactDiffViewer from 'react-diff-viewer';
import LatexMixedText from '../../common/LatexMixedText';
import LatexTextField from '../../common/LatexTextField';
import CodingEditor from '../../Flashcards/Flashcardform/Flashcardforms/CodingEditor';
import DatePicker from '@mui/lab/DatePicker';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import 'prismjs/themes/prism.css';
import { highlight, languages } from 'prismjs/components/prism-core';
import 'prismjs/components/prism-clike.js';
import 'prismjs/components/prism-javascript.js';
import 'prismjs/components/prism-markup.js';
import 'prismjs/components/prism-jsx.js';
import 'prismjs/components/prism-c';
import 'prismjs/components/prism-cpp';
import 'prismjs/components/prism-bash';

import {
  IMAGEPRESENTATION_TYPE_ALL,
  IMAGEPRESENTATION_TYPE_FIRST,
  IMAGEPRESENTATION_TYPE_RANDOM,
} from '../../../constants/flashcardTypes';
import { parseImageFieldValue } from '../../../common/imageFieldUtils';

const reduceStringAccToFormat = (format, value) => {
  switch (format) {
    case 'year': return String(value).substr(0, 4);
    case 'year month': return String(value).substr(0, 7);
    case 'year month day': return String(value).substr(0, 10);
    default: return String(value).substr(0, 10);
  }
};

const getTimezoneOffset = (value) => value.getTimezoneOffset() * 60000;

const makeLocalAppearUTC = (value) => {
  if (!value) return undefined;
  const dateTime = new Date(value);
  return new Date(dateTime.getTime() + getTimezoneOffset(dateTime));
};

const localToUTC = (dateTime) =>
  new Date(dateTime.getTime() - getTimezoneOffset(dateTime));

const getLanguage = (syntax) => {
  switch (syntax) {
    case 'Javascript': return languages.js;
    case 'JSX': return languages.jsx;
    case 'C++': return languages.cpp;
    case 'Bash': return languages.bash;
    default: return null;
  }
};

const diffStyles = {
  variables: {
    highlightBackground: '#fefed5',
    highlightGutterBackground: '#ffcd3c',
  },
  line: {
    padding: '10px 2px',
    '&:hover': { background: '#a26ea1' },
  },
};

const CORRECT_STYLE = {
  margin: '4px 0',
  padding: '4px 8px',
  borderLeft: '4px solid #4caf50',
  background: '#e8f5e9',
};
const WRONG_STYLE = {
  margin: '4px 0',
  padding: '4px 8px',
  borderLeft: '4px solid #f44336',
  background: '#ffebee',
};

const playSound = (base64) => {
  if (!base64) return;
  const snd = new Audio(base64);
  snd.play();
};

const normalize = (s, casesensitive) =>
  casesensitive ? s.trim() : s.toLowerCase().trim();

const TestformGeneric = (
  {
    flashcardData,
    submit,
    allowCtrlEnterSubmit = true,
    flashcardType,
  },
  ref,
) => {
  const typeFields = flashcardType?.fields || [];
  const cardFields = flashcardData.fields || [];
  const cs = flashcardData.casesensitive;

  const fieldByKey = (key) => cardFields.find((f) => f.key === key);
  const getVal = (idx) => {
    const tf = typeFields[idx];
    if (!tf) return '';
    if (cardFields.length === typeFields.length) {
      return cardFields[idx]?.value ?? '';
    }
    return fieldByKey(tf.key)?.value ?? '';
  };

  const imageValuesKey = typeFields
    .map((tf, i) => {
      if (tf.fieldType !== 'image' || tf.role !== 'question') return '';
      const v = getVal(i) || '';
      return `${v.length}:${v.slice(0, 48)}:${v.slice(-48)}`;
    })
    .join('|');

  const typeLayoutKey = typeFields
    .map((f) => `${f.key}:${f.fieldType}:${f.role}`)
    .join('|');

  const questionImageDisplayUrls = useMemo(() => {
    const rawPresentation =
      flashcardData.imagepresentationtype || IMAGEPRESENTATION_TYPE_RANDOM;
    const presentation = [
      IMAGEPRESENTATION_TYPE_ALL,
      IMAGEPRESENTATION_TYPE_FIRST,
      IMAGEPRESENTATION_TYPE_RANDOM,
    ].includes(rawPresentation)
      ? rawPresentation
      : IMAGEPRESENTATION_TYPE_RANDOM;
    const out = {};
    typeFields.forEach((tf, idx) => {
      if (tf.fieldType !== 'image' || tf.role !== 'question') return;
      let raw = '';
      if (cardFields.length === typeFields.length) {
        raw = cardFields[idx]?.value ?? '';
      } else {
        raw = cardFields.find((f) => f.key === tf.key)?.value ?? '';
      }
      const urls = parseImageFieldValue(raw);
      if (!urls.length) {
        out[idx] = [];
        return;
      }
      if (urls.length === 1) {
        out[idx] = urls;
        return;
      }
      if (presentation === IMAGEPRESENTATION_TYPE_ALL) {
        out[idx] = urls;
      } else if (presentation === IMAGEPRESENTATION_TYPE_FIRST) {
        out[idx] = [urls[0]];
      } else {
        out[idx] = [urls[Math.floor(Math.random() * urls.length)]];
      }
    });
    return out;
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps -- cardFields/typeFields captured via imageValuesKey + typeLayoutKey
  [
    flashcardData._id,
    flashcardData.imagepresentationtype,
    imageValuesKey,
    typeLayoutKey,
  ]);

  const solvableTypes = ['text', 'code', 'date', 'enumeration', 'quiz', 'dropdown', 'imagepos'];
  const solvableIndices = typeFields.reduce((acc, f, i) => {
    if (f.role === 'solution' && solvableTypes.includes(f.fieldType)) acc.push(i);
    return acc;
  }, []);

  const [userInputs, setUserInputs] = useState({});
  const [codeState, setCodeState] = useState({ code: '', testState: [] });

  const hasCodeField = typeFields.some(
    (f) => f.role === 'solution' && f.fieldType === 'code',
  );

  useEffect(() => {
    setUserInputs({});

    if (hasCodeField) {
      const codeIdx = typeFields.findIndex(
        (f) => f.role === 'solution' && f.fieldType === 'code',
      );
      const original = getVal(codeIdx);
      const rawTest = JSON.parse(JSON.stringify(flashcardData.test || []));
      let newValue = original;
      const newTestState = [...rawTest];

      for (let i = 0; i < newTestState.length; i++) {
        if (i === 0) {
          newValue = original.slice(0, newTestState[0].start[0]) + ' ';
        } else {
          newValue +=
            original.slice(newTestState[i - 1].end[0], newTestState[i].start[0]) + ' ';
        }
      }
      if (newTestState.length > 0) {
        newValue +=
          original.slice(newTestState[newTestState.length - 1].end[0], original.length);
      }
      for (let i = 0; i < newTestState.length; i++) {
        const size = newTestState[i].end[0] - newTestState[i].start[0] - 1;
        newTestState[i].end[0] = newTestState[i].start[0] + 1;
        for (let k = i + 1; k < newTestState.length; k++) {
          newTestState[k].start[0] -= size;
          newTestState[k].end[0] -= size;
        }
      }
      setCodeState({ code: newValue, testState: newTestState });
    }
  }, [flashcardData]);

  const updateInput = (index, value) => {
    setUserInputs((prev) => ({ ...prev, [index]: value }));
  };

  const solveField = (i) => {
    const fieldDef = typeFields[i];
    const expected = getVal(i);

    switch (fieldDef.fieldType) {
      case 'text': {
        const actual = userInputs[i] || '';
        return normalize(actual, cs) === normalize(expected, cs);
      }
      case 'code': {
        if (!codeState.testState.length) return false;
        return codeState.testState
          .map((test, ti) => {
            const orig = expected.substring(
              flashcardData.test[ti]?.start?.[0] ?? 0,
              flashcardData.test[ti]?.end?.[0] ?? 0,
            );
            const filled = codeState.code.substring(test.start[0], test.end[0]);
            return cs
              ? orig.trim() === filled.trim()
              : orig.toLowerCase().trim() === filled.toLowerCase().trim();
          })
          .every(Boolean);
      }
      case 'date': {
        let dateObj = { format: 'year', start: '', end: '', isrange: false };
        try { dateObj = { ...dateObj, ...JSON.parse(expected) }; } catch { return false; }
        let userDate = { start: '', end: '' };
        try { userDate = { ...userDate, ...JSON.parse(userInputs[i] || '{}') }; } catch { return false; }
        const fmt = dateObj.format;
        let result =
          reduceStringAccToFormat(fmt, userDate.start) ===
          reduceStringAccToFormat(fmt, dateObj.start);
        if (dateObj.isrange) {
          result =
            result &&
            reduceStringAccToFormat(fmt, userDate.end) ===
              reduceStringAccToFormat(fmt, dateObj.end);
        }
        return result;
      }
      case 'enumeration': {
        const expectedItems = expected.split('\n').map((s) => s.trim()).filter(Boolean);
        let actualItems = [];
        try { actualItems = JSON.parse(userInputs[i] || '[]'); } catch { actualItems = []; }
        actualItems = actualItems.map((s) => (s || '').trim()).filter(Boolean);
        if (expectedItems.length !== actualItems.length) return false;
        const eSorted = expectedItems.map((s) => normalize(s, cs)).sort();
        const aSorted = actualItems.map((s) => normalize(s, cs)).sort();
        return eSorted.every((v, idx) => v === aSorted[idx]);
      }
      case 'quiz': {
        let options = [];
        try { options = JSON.parse(expected); } catch { return false; }
        let selected = [];
        try { selected = JSON.parse(userInputs[i] || '[]'); } catch { return false; }
        const correctIdx = options.reduce(
          (acc, opt, idx) => (opt.correct ? [...acc, idx] : acc),
          [],
        );
        return (
          correctIdx.length === selected.length &&
          correctIdx.every((idx) => selected.includes(idx))
        );
      }
      case 'dropdown': {
        let options = [];
        try { options = JSON.parse(expected); } catch { return false; }
        const correctIdx = options.findIndex((opt) => !!opt.correct);
        if (correctIdx < 0) return false;
        const selected = Number(userInputs[i]);
        if (!Number.isInteger(selected)) return false;
        return selected === correctIdx;
      }
      case 'imagepos': {
        let expectedPos = { x: 0, y: 0, radius: 20 };
        try { expectedPos = JSON.parse(expected); } catch { return false; }
        let actualPos = null;
        try { actualPos = JSON.parse(userInputs[i] || 'null'); } catch { return false; }
        if (!actualPos) return false;
        const radius = Number.isFinite(Number(expectedPos.radius))
          ? Math.max(0, Number(expectedPos.radius))
          : 20;
        const dx = Number(expectedPos.x) - Number(actualPos.x);
        const dy = Number(expectedPos.y) - Number(actualPos.y);
        return Math.sqrt(dx * dx + dy * dy) <= radius;
      }
      default:
        return true;
    }
  };

  const renderSolutionDisplay = (fieldDef, i) => {
    const value = getVal(i);
    const label = fieldDef.label || `Field ${i + 1}`;

    switch (fieldDef.fieldType) {
      case 'text': {
        const actual = userInputs[i] || '';
        const isCorrect = normalize(actual, cs) === normalize(value, cs);
        if (isCorrect) {
          return (
            <div key={i} style={CORRECT_STYLE}>
              <strong>{label}:</strong> <LatexMixedText text={value} />
            </div>
          );
        }
        return (
          <div key={i} style={{ margin: '4px 0' }}>
            <strong>{label}:</strong>
            <ReactDiffViewer
              styles={diffStyles}
              oldValue={actual}
              newValue={value}
              splitView={true}
              hideLineNumbers={true}
            />
          </div>
        );
      }
      case 'code': {
        const isCorrect = solveField(i);
        return (
          <div key={i} style={isCorrect ? CORRECT_STYLE : WRONG_STYLE}>
            <strong>{label}:</strong>
            <pre
              style={{
                fontFamily: '"Fira Code", "Fira Mono", monospace',
                fontSize: 13,
                background: isCorrect ? '#e8f5e9' : '#ffebee',
                padding: 8,
                overflowX: 'auto',
                whiteSpace: 'pre-wrap',
              }}
            >
              {value}
            </pre>
          </div>
        );
      }
      case 'date': {
        let dateObj = { format: 'year', start: '', end: '', isrange: false };
        try { dateObj = { ...dateObj, ...JSON.parse(value) }; } catch { /* defaults */ }
        let userDate = { start: '', end: '' };
        try { userDate = { ...userDate, ...JSON.parse(userInputs[i] || '{}') }; } catch { /* defaults */ }
        const fmt = dateObj.format;
        const isCorrectDate = solveField(i);
        const expectedStr = dateObj.isrange
          ? `${reduceStringAccToFormat(fmt, dateObj.start)}  –  ${reduceStringAccToFormat(fmt, dateObj.end)}`
          : reduceStringAccToFormat(fmt, dateObj.start);
        const actualStr = dateObj.isrange
          ? `${reduceStringAccToFormat(fmt, userDate.start)}  –  ${reduceStringAccToFormat(fmt, userDate.end)}`
          : reduceStringAccToFormat(fmt, userDate.start);
        return (
          <div key={i} style={isCorrectDate ? CORRECT_STYLE : WRONG_STYLE}>
            <strong>{label}:</strong>
            <div>Expected: {expectedStr}</div>
            {!isCorrectDate && <div>Your answer: {actualStr || '(none)'}</div>}
          </div>
        );
      }
      case 'enumeration': {
        const expectedItems = value.split('\n').map((s) => s.trim()).filter(Boolean);
        let actualItems = [];
        try { actualItems = JSON.parse(userInputs[i] || '[]'); } catch { actualItems = []; }
        actualItems = actualItems.map((s) => (s || '').trim()).filter(Boolean);
        const matchedExpected = new Set();
        const actualResults = actualItems.map((item) => {
          const idx = expectedItems.findIndex(
            (e, ei) => !matchedExpected.has(ei) && normalize(e, cs) === normalize(item, cs),
          );
          if (idx >= 0) {
            matchedExpected.add(idx);
            return { item, correct: true };
          }
          return { item, correct: false };
        });
        const missingItems = expectedItems.filter((_, idx) => !matchedExpected.has(idx));

        return (
          <div key={i} style={{ margin: '4px 0' }}>
            <strong>{label}:</strong>
            {actualResults.map((r, idx) => (
              <div key={`a_${idx}`} style={r.correct ? CORRECT_STYLE : WRONG_STYLE}>
                {r.correct ? '\u2713' : '\u2717'} {r.item}
              </div>
            ))}
            {missingItems.map((item, idx) => (
              <div key={`m_${idx}`} style={WRONG_STYLE}>
                (missing) {item}
              </div>
            ))}
          </div>
        );
      }
      case 'quiz': {
        let options = [];
        try { options = JSON.parse(value); } catch { options = []; }
        let selected = [];
        try { selected = JSON.parse(userInputs[i] || '[]'); } catch { selected = []; }
        return (
          <div key={i} style={{ margin: '4px 0' }}>
            <strong>{label}:</strong>
            {options.map((opt, idx) => {
              const isCorrect = !!opt.correct;
              const wasSelected = selected.includes(idx);
              const ok = isCorrect === wasSelected;
              return (
                <div key={idx} style={ok ? CORRECT_STYLE : WRONG_STYLE}>
                  {wasSelected ? '\u2611' : '\u2610'}{' '}
                  <LatexMixedText text={opt.text} />
                  {isCorrect && !wasSelected && <span style={{ color: '#4caf50' }}> (correct answer)</span>}
                  {!isCorrect && wasSelected && <span style={{ color: '#f44336' }}> (wrong)</span>}
                </div>
              );
            })}
          </div>
        );
      }
      case 'dropdown': {
        let options = [];
        try { options = JSON.parse(value); } catch { options = []; }
        const correctIdx = options.findIndex((opt) => !!opt.correct);
        const selected = Number(userInputs[i]);
        return (
          <div key={i} style={{ margin: '4px 0' }}>
            <strong>{label}:</strong>
            {options.map((opt, idx) => {
              const isCorrect = idx === correctIdx;
              const wasSelected = idx === selected;
              const ok = isCorrect === wasSelected;
              return (
                <div key={idx} style={ok ? CORRECT_STYLE : WRONG_STYLE}>
                  {wasSelected ? '\u25c9' : '\u25ef'}{' '}
                  <LatexMixedText text={opt.text} />
                  {isCorrect && !wasSelected && <span style={{ color: '#4caf50' }}> (correct answer)</span>}
                  {!isCorrect && wasSelected && <span style={{ color: '#f44336' }}> (wrong)</span>}
                </div>
              );
            })}
          </div>
        );
      }
      case 'imagepos': {
        let expectedPos = { x: 0, y: 0, radius: 20, image: '' };
        try { expectedPos = JSON.parse(value); } catch { /* defaults */ }
        let actualPos = null;
        try { actualPos = JSON.parse(userInputs[i] || 'null'); } catch { /* null */ }
        const radius = Number.isFinite(Number(expectedPos.radius))
          ? Math.max(0, Number(expectedPos.radius))
          : 20;
        const imgVal = expectedPos.image || '';

        return (
          <div key={i} style={{ margin: '4px 0' }}>
            <strong>{label}:</strong>
            {imgVal && (
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <img src={imgVal} height="300" alt="" />
                <div
                  title="Expected radius"
                  style={{
                    position: 'absolute',
                    left: expectedPos.x - radius,
                    top: expectedPos.y - radius,
                    width: radius * 2,
                    height: radius * 2,
                    borderRadius: '50%',
                    background: 'rgba(76, 175, 80, 0.16)',
                    border: '1px solid rgba(76, 175, 80, 0.6)',
                    pointerEvents: 'none',
                  }}
                />
                <div
                  title="Expected"
                  style={{
                    position: 'absolute',
                    left: expectedPos.x - 8,
                    top: expectedPos.y - 8,
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    background: 'rgba(76, 175, 80, 0.7)',
                    border: '2px solid #4caf50',
                    pointerEvents: 'none',
                  }}
                />
                {actualPos && (
                  <div
                    title="Your answer"
                    style={{
                      position: 'absolute',
                      left: actualPos.x - 8,
                      top: actualPos.y - 8,
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      background: 'rgba(244, 67, 54, 0.7)',
                      border: '2px solid #f44336',
                      pointerEvents: 'none',
                    }}
                  />
                )}
              </div>
            )}
            <div style={{ fontSize: '0.85em', color: '#888' }}>
              Expected: ({expectedPos.x}, {expectedPos.y}), Radius: {radius}
              {actualPos && <span> | Yours: ({actualPos.x}, {actualPos.y})</span>}
            </div>
          </div>
        );
      }
      case 'sound': {
        if (!value) return null;
        return (
          <div key={i} style={{ margin: '4px 0' }}>
            <strong>{label}:</strong>{' '}
            <VolumeUpIcon
              style={{ fill: 'green', cursor: 'pointer', verticalAlign: 'middle' }}
              onClick={() => playSound(value)}
            />
          </div>
        );
      }
      default:
        return null;
    }
  };

  useImperativeHandle(ref, () => ({
    solve() {
      if (solvableIndices.length === 0) return false;
      return solvableIndices.every(solveField);
    },
    solution() {
      return (
        <div>
          {typeFields.map((fieldDef, i) => {
            const value = getVal(i);
            const label = fieldDef.label || `Field ${i + 1}`;

            if (fieldDef.role === 'question') {
              return (
                <div key={i} style={{ margin: '4px 0', opacity: 0.7 }}>
                  <strong>{label}:</strong>{' '}
                  {fieldDef.fieldType === 'image' &&
                  (questionImageDisplayUrls[i] || []).length ? (
                    <span>
                      {(questionImageDisplayUrls[i] || []).map((src, ui) => (
                        <img
                          key={ui}
                          src={src}
                          height="120"
                          alt=""
                          style={{ marginRight: 8, verticalAlign: 'top' }}
                        />
                      ))}
                    </span>
                  ) : fieldDef.fieldType === 'sound' && value ? (
                    <VolumeUpIcon
                      style={{ fill: 'green', cursor: 'pointer', verticalAlign: 'middle' }}
                      onClick={() => playSound(value)}
                    />
                  ) : (
                    <LatexMixedText text={value} />
                  )}
                </div>
              );
            }

            return renderSolutionDisplay(fieldDef, i);
          })}
        </div>
      );
    },
    solutionInfo() {
      const infotext = flashcardData.solutions?.[0]?.infotext || '';
      return (
        <div>
          <LatexMixedText text={infotext} block />
        </div>
      );
    },
  }));

  const keyHandler = (e) => {
    if (
      allowCtrlEnterSubmit &&
      (e.code === 'Enter' || e.code === 'NumpadEnter') &&
      e.ctrlKey
    ) {
      submit();
    }
  };

  let firstInputRendered = false;

  const renderQuestionField = (fieldDef, index) => {
    const value = getVal(index);
    switch (fieldDef.fieldType) {
      case 'text':
        return (
          <p key={`q_${index}`}>
            <LatexMixedText text={value} />
          </p>
        );
      case 'image': {
        const urls = questionImageDisplayUrls[index] || [];
        if (!urls.length) return null;
        return (
          <div key={`q_${index}`} style={{ border: '2px solid rgba(0,0,0,1.0)' }}>
            {urls.map((src, ui) => (
              <img
                key={ui}
                src={src}
                height="300"
                alt=""
                style={{
                  display: 'block',
                  marginBottom: ui < urls.length - 1 ? 8 : 0,
                }}
              />
            ))}
          </div>
        );
      }
      case 'code':
        return (
          <pre
            key={`q_${index}`}
            style={{
              fontFamily: '"Fira Code", "Fira Mono", monospace',
              fontSize: 13,
              background: '#f5f5f5',
              padding: 8,
              overflowX: 'auto',
            }}
          >
            {value}
          </pre>
        );
      case 'sound':
        return null;
      default:
        return (
          <p key={`q_${index}`}>
            <LatexMixedText text={value} />
          </p>
        );
    }
  };

  const renderSolutionField = (fieldDef, index) => {
    if (fieldDef.fieldType === 'sound') {
      return null;
    }

    const isFirst = !firstInputRendered;
    if (
      fieldDef.fieldType === 'text' ||
      fieldDef.fieldType === 'code' ||
      fieldDef.fieldType === 'date' ||
      fieldDef.fieldType === 'enumeration' ||
      fieldDef.fieldType === 'dropdown'
    ) {
      if (isFirst) firstInputRendered = true;
    }

    switch (fieldDef.fieldType) {
      case 'text': {
        return (
          <div key={`s_${index}`}>
            <LatexTextField
              value={userInputs[index] || ''}
              onChange={(e) => updateInput(index, e.target.value)}
              fullWidth
              multiline
              maxRows={10}
              autoFocus={isFirst}
              onKeyPress={keyHandler}
            />
          </div>
        );
      }
      case 'code': {
        const syntax = flashcardData.syntax || '';
        const lang = getLanguage(syntax);
        return (
          <div key={`s_${index}`}>
            <CodingEditor
              value={codeState.code}
              onValueChange={(code) =>
                setCodeState((prev) => ({ ...prev, code }))
              }
              highlight={(code) => (lang ? highlight(code, lang) : code)}
              solve={true}
              testState={codeState.testState}
              setTestState={({ test }) =>
                setCodeState((prev) => ({ ...prev, testState: test }))
              }
              autoFocus
              onKeyDown={(e) => {
                if (
                  allowCtrlEnterSubmit &&
                  (e.code === 'Enter' || e.code === 'NumpadEnter') &&
                  e.ctrlKey
                ) {
                  submit();
                }
              }}
            />
          </div>
        );
      }
      case 'date': {
        let dateObj = { format: 'year', start: '', end: '', isrange: false };
        try { dateObj = { ...dateObj, ...JSON.parse(getVal(index) || '{}') }; } catch { /* defaults */ }
        let userDate = { start: '', end: '' };
        try { userDate = { ...userDate, ...JSON.parse(userInputs[index] || '{}') }; } catch { /* defaults */ }
        const views = dateObj.format.split(/\s+/);
        const valueStart = userDate.start ? makeLocalAppearUTC(userDate.start) : new Date();
        const valueEnd = userDate.end ? makeLocalAppearUTC(userDate.end) : new Date();

        const updateDatePart = (part, dateValue) => {
          const utc = JSON.parse(JSON.stringify(localToUTC(dateValue)));
          updateInput(index, JSON.stringify({ ...userDate, [part]: utc }));
        };

        return (
          <div key={`s_${index}`}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <div style={{ marginBottom: 8 }}>
                <span style={{ marginRight: 8 }}>Start:</span>
                <DatePicker
                  views={views}
                  label={dateObj.format}
                  value={valueStart}
                  onChange={(val) => updateDatePart('start', val)}
                  renderInput={(params) => <TextField {...params} helperText={null} />}
                />
              </div>
              {dateObj.isrange && (
                <div>
                  <span style={{ marginRight: 8 }}>End:</span>
                  <DatePicker
                    views={views}
                    label={dateObj.format}
                    value={valueEnd}
                    onChange={(val) => updateDatePart('end', val)}
                    renderInput={(params) => <TextField {...params} helperText={null} />}
                  />
                </div>
              )}
            </LocalizationProvider>
          </div>
        );
      }
      case 'enumeration': {
        let items = [];
        try { items = JSON.parse(userInputs[index] || '[]'); } catch { items = []; }
        if (!items.length) items = [''];

        const updateItems = (newItems) => {
          updateInput(index, JSON.stringify(newItems));
        };
        const updateItem = (itemIdx, val) => {
          const updated = [...items];
          updated[itemIdx] = val;
          updateItems(updated);
        };

        const focusEnumItem = (itemTarget) => {
          setTimeout(() => {
            const container = document.getElementById(`enum-test-${index}`);
            if (!container) return;
            const inputs = container.querySelectorAll('input, textarea');
            if (inputs[itemTarget]) inputs[itemTarget].focus();
          }, 0);
        };

        return (
          <div key={`s_${index}`} id={`enum-test-${index}`}>
            {items.map((item, itemIdx) => (
              <div
                key={itemIdx}
                style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}
              >
                <span style={{ minWidth: 24, textAlign: 'right', color: '#888' }}>
                  {itemIdx + 1}.
                </span>
                <LatexTextField
                  value={item}
                  onChange={(e) => updateItem(itemIdx, e.target.value)}
                  fullWidth
                  autoFocus={isFirst && itemIdx === 0}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.ctrlKey && !e.shiftKey) {
                      e.preventDefault();
                      const newItems = [...items];
                      newItems.splice(itemIdx + 1, 0, '');
                      updateItems(newItems);
                      focusEnumItem(itemIdx + 1);
                    }
                    keyHandler(e);
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => {
                    const updated = items.filter((_, i) => i !== itemIdx);
                    updateItems(updated.length ? updated : ['']);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </div>
            ))}
            <Button
              size="small"
              startIcon={<AddCircleIcon />}
              onClick={() => updateItems([...items, ''])}
            >
              Add item
            </Button>
          </div>
        );
      }
      case 'quiz': {
        let options = [];
        try { options = JSON.parse(getVal(index) || '[]'); } catch { options = []; }
        let selected = [];
        try { selected = JSON.parse(userInputs[index] || '[]'); } catch { selected = []; }

        const toggleOption = (optIdx) => {
          const newSelected = selected.includes(optIdx)
            ? selected.filter((i) => i !== optIdx)
            : [...selected, optIdx];
          updateInput(index, JSON.stringify(newSelected));
        };

        return (
          <div key={`s_${index}`}>
            {options.map((opt, optIdx) => (
              <div key={optIdx} style={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox
                  checked={selected.includes(optIdx)}
                  onChange={() => toggleOption(optIdx)}
                />
                <LatexMixedText text={opt.text} />
              </div>
            ))}
          </div>
        );
      }
      case 'dropdown': {
        let options = [];
        try { options = JSON.parse(getVal(index) || '[]'); } catch { options = []; }
        if (!Array.isArray(options) || !options.length) options = [{ text: '', correct: true }];
        const selectedRaw = userInputs[index];
        const selected = selectedRaw === undefined || selectedRaw === '' ? '' : Number(selectedRaw);

        return (
          <div key={`s_${index}`}>
            <Select
              value={selected}
              onChange={(e) => updateInput(index, String(e.target.value))}
              displayEmpty
              fullWidth
              autoFocus={isFirst}
            >
              <MenuItem value="">
                <em>Select an option</em>
              </MenuItem>
              {options.map((opt, optIdx) => (
                <MenuItem key={optIdx} value={optIdx}>
                  <LatexMixedText text={opt.text} />
                </MenuItem>
              ))}
            </Select>
          </div>
        );
      }
      case 'imagepos': {
        let expectedPos = { x: 0, y: 0, radius: 20, image: '' };
        try { expectedPos = { ...expectedPos, ...JSON.parse(getVal(index) || '{}') }; } catch { /* defaults */ }
        const imgVal = expectedPos.image || '';
        let userPos = null;
        try { userPos = JSON.parse(userInputs[index] || 'null'); } catch { userPos = null; }

        const handleImageClick = (e) => {
          const rect = e.target.getBoundingClientRect();
          const x = Math.round(e.clientX - rect.left);
          const y = Math.round(e.clientY - rect.top);
          updateInput(index, JSON.stringify({ x, y }));
        };

        return (
          <div key={`s_${index}`}>
            <div style={{ color: '#888', fontSize: '0.85em', marginBottom: 4 }}>
              Click on the image to mark your answer
            </div>
            {imgVal && (
              <div style={{ position: 'relative', display: 'inline-block', cursor: 'crosshair' }}>
                <img src={imgVal} height="300" alt="" onClick={handleImageClick} />
                {userPos && (
                  <div
                    style={{
                      position: 'absolute',
                      left: userPos.x - 8,
                      top: userPos.y - 8,
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      background: 'rgba(255, 0, 0, 0.7)',
                      border: '2px solid red',
                      pointerEvents: 'none',
                    }}
                  />
                )}
              </div>
            )}
            {userPos && (
              <div style={{ fontSize: '0.85em', color: '#888' }}>
                Position: ({userPos.x}, {userPos.y})
              </div>
            )}
          </div>
        );
      }
      default:
        return null;
    }
  };

  return (
    <div>
      <Card>
        {typeFields.map((fieldDef, index) => {
          if (fieldDef.role === 'question') {
            return renderQuestionField(fieldDef, index);
          }
          return renderSolutionField(fieldDef, index);
        })}
      </Card>
    </div>
  );
};

export default forwardRef(TestformGeneric);
